import { useMemo } from "react"

import { IconCheck, IconColumns2, IconLock } from "@tabler/icons-react"
import { VisibilityState } from "@tanstack/react-table"
import { useLocalStorage } from "usehooks-ts"
import z from "zod"

import { cn } from "@/lib/utils"

import { Button } from "../../Button"
import { Command, CommandInput, CommandItem, CommandList } from "../../cn/command"
import { CopyButton } from "../../CopyToClipboard"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../../DropdownMenu"
import { Input } from "../../Input"
import { Textarea } from "../../Textarea"
import { toast } from "../../Toast"
import { Tooltip } from "../../Tooltip"
import { KEBAB_MENU_COLUMN_ID, SELECTABLE_COLUMN_ID } from "../DataTable"
import { LE_TableInstance } from "../types"

const COLUMNS_VISIBILITY_BUTTON_ID_SUFFIX = "column-visibility-button"

export function ColumnsVisibilityButton<T extends Record<string, any> = object>({
  table,
}: {
  table: LE_TableInstance<T>
}) {
  const columns = table.getAllFlatColumns()
  const allColumns = useMemo(() => {
    return columns
      .filter((col) => col.columns.length === 0 && col.getCanHide())
      .sort((a, b) => {
        const aLabel = a.columnDef.meta?.textLabel ?? a.columnDef.header ?? ""
        const bLabel = b.columnDef.meta?.textLabel ?? b.columnDef.header ?? ""

        return aLabel > bLabel ? 1 : -1
      })
  }, [columns])

  const { columnVisibility } = table.getState()
  const [savedConfigs, setSavedConfigs] = useLocalStorage<
    Record<string, VisibilityState>
  >(`table-${table.tableId}-visibility`, {})
  const noSavedConfigs = Object.entries(savedConfigs).length === 0

  return (
    <DropdownMenu>
      <Tooltip content="Show/hide columns">
        <DropdownMenuTrigger asChild>
          <Button
            id={table.getElementId(COLUMNS_VISIBILITY_BUTTON_ID_SUFFIX)}
            variant="neutral"
            iconOnly
            icon={IconColumns2}
            aria-label="show/hide columns"
            className="h-fit"
          />
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuContent>
        <DropdownMenuItem
          onSelect={(e) => {
            // don't close the menu on select
            e.preventDefault()
            table.toggleAllColumnsVisible(!table.getIsAllColumnsVisible())
          }}
        >
          <Button variant="neutral" className="w-full">
            {table.getIsAllColumnsVisible() ? "Hide all" : "Show all"}
          </Button>
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Import/Export</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuLabel>Current config</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Export</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuLabel>
                  Share the following code:
                  <CopyButton text={btoa(JSON.stringify(columnVisibility))} />
                </DropdownMenuLabel>
                <pre className="max-h-52 max-w-sm overflow-auto p-2 text-body2 break-all whitespace-break-spaces">
                  {btoa(JSON.stringify(columnVisibility))}
                </pre>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Import</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuLabel>Paste the exported code below:</DropdownMenuLabel>
                <form
                  className="flex w-96 flex-col items-start gap-2 p-2 pt-0"
                  onSubmit={(event) => {
                    event.preventDefault()
                    try {
                      const code = new FormData(event.currentTarget).get(
                        "code",
                      ) as string
                      const json = JSON.parse(atob(code))
                      const parsed = z.record(z.string(), z.boolean()).parse(json)
                      table.setColumnVisibility(parsed)
                      toast.success("Configuration imported successfully!")
                      event.currentTarget.reset()
                    } catch {
                      toast.error("Invalid code")
                    }
                  }}
                >
                  <Textarea name="code" className="h-52 text-body2" />
                  <Button type="submit" variant="primary" className="self-end">
                    Import
                  </Button>
                </form>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Saved configs</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuLabel>Configs</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger disabled={noSavedConfigs}>
                Load
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {Object.entries(savedConfigs).map(([label, value]) => (
                  <DropdownMenuItem
                    key={label}
                    onSelect={(event) => {
                      event.preventDefault()
                      toast.success(`Loaded configuration "${label}"`)
                      table.setColumnVisibility(value)
                    }}
                  >
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Save</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuLabel>Save as:</DropdownMenuLabel>
                <form
                  className="flex w-48 flex-col items-start gap-2 p-2 pt-0"
                  onSubmit={(event) => {
                    event.preventDefault()
                    const formData = new FormData(event.currentTarget)
                    const label = formData.get("save-as") as string
                    setSavedConfigs({
                      ...savedConfigs,
                      [label]: columnVisibility,
                    })
                    event.currentTarget.reset()
                    toast.success(`Saved configuration "${label}"`)
                  }}
                >
                  <Input autoFocus name="save-as" className="text-body2" />
                  <Button type="submit" variant="primary" className="self-end">
                    Save config
                  </Button>
                </form>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub
              onOpenChange={(open) => {
                if (Object.keys(savedConfigs).length === 0) return true
                return open
              }}
            >
              <DropdownMenuSubTrigger disabled={noSavedConfigs}>
                Delete
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {Object.entries(savedConfigs).map(([label]) => (
                  <DropdownMenuItem
                    onSelect={(event) => {
                      event.preventDefault()
                      const { [label]: _, ...rest } = savedConfigs
                      setSavedConfigs(rest)
                      toast.success(`Deleted configuration "${label}"`)
                    }}
                    key={label}
                  >
                    {label}
                  </DropdownMenuItem>
                ))}
                {noSavedConfigs && (
                  <DropdownMenuItem
                    disabled
                    className="text-body2 data-[disabled]:pointer-events-auto"
                  >
                    No configs saved
                  </DropdownMenuItem>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger disabled={noSavedConfigs}>
                Export
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuLabel>
                  Share the following code:
                  <CopyButton text={btoa(JSON.stringify(savedConfigs))} />
                </DropdownMenuLabel>
                <pre className="max-h-52 max-w-sm overflow-auto p-2 text-body2 break-all whitespace-break-spaces">
                  {btoa(JSON.stringify(savedConfigs))}
                </pre>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Import</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuLabel>Paste the exported code below:</DropdownMenuLabel>
                <form
                  className="flex w-96 flex-col items-start gap-2 p-2 pt-0"
                  onSubmit={(event) => {
                    event.preventDefault()
                    try {
                      const code = new FormData(event.currentTarget).get(
                        "code",
                      ) as string
                      const json = JSON.parse(atob(code))
                      console.log(json)
                      const parsed = z
                        .record(z.string(), z.record(z.string(), z.boolean()))
                        .parse(json)
                      setSavedConfigs(parsed)
                      toast.success("Saved configurations imported successfully!")
                      event.currentTarget.reset()
                    } catch {
                      toast.error("Invalid code")
                    }
                  }}
                >
                  <Textarea name="code" className="h-52 text-body2" />
                  <Button type="submit" variant="primary" className="self-end">
                    Import
                  </Button>
                </form>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />

        <Command>
          <CommandInput
            rootClassName="pb-1 px-2"
            className="h-8 py-0"
            placeholder="Search..."
            autoFocus
            onKeyDown={(e) => {
              // Stop propagation of non-arrow keys to prevent triggering menu navigation shortcuts while searching
              if (
                !["Enter", "ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(
                  e.key,
                )
              ) {
                e.stopPropagation()
              }
            }}
          />
          <CommandList className="mt-1">
            {allColumns.map((col) => {
              if (
                // filter column groups
                col.columns.length > 0 ||
                !col.columnDef.header ||
                // filter built-in columns like selectable and kebab menu
                (col.columnDef.id &&
                  [SELECTABLE_COLUMN_ID, KEBAB_MENU_COLUMN_ID].includes(
                    col.columnDef.id,
                  ))
              )
                return null

              const label = col.columnDef.meta?.textLabel ?? col.columnDef.header
              if (typeof label !== "string") {
                throw new Error(
                  "column `header` must be a string, or `meta.textLabel` must be set.",
                )
              }

              return (
                <CommandItem
                  key={label}
                  className="mx-1 flex gap-2"
                  disabled={!col.columnDef.enableHiding}
                  onSelect={() => {
                    col.toggleVisibility()
                  }}
                >
                  <IconCheck
                    size={14}
                    className={cn({ ["invisible"]: !col.getIsVisible() })}
                  />{" "}
                  {label}
                  {col.columnDef.meta?.adminOnly && <IconLock />}
                </CommandItem>
              )
            })}
          </CommandList>
        </Command>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
